import React, { useCallback, useEffect, useState } from 'react';
import { Card, Badge, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { helpdeskAPI } from '../services/api';

const severityBadgeVariant = (severity) => {
  switch (severity) {
    case 'critical':
      return 'danger';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    default:
      return 'secondary';
  }
};

const ReviewQueue = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [saveAsKnowledge, setSaveAsKnowledge] = useState({});
  const [sendingId, setSendingId] = useState(null);

  const fetchQueue = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      const response = await helpdeskAPI.getReviewQueue();
      setRows(response.data || []);
    } catch (err) {
      console.error('Error loading review queue:', err);
      setError('Failed to load the review queue');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(() => fetchQueue(true), 15000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  const shouldSaveAsKnowledge = (rowId) =>
    saveAsKnowledge[rowId] === undefined ? true : saveAsKnowledge[rowId];

  const toggleSaveAsKnowledge = (rowId) => {
    setSaveAsKnowledge((prev) => ({ ...prev, [rowId]: !shouldSaveAsKnowledge(rowId) }));
  };

  const sendReply = async (row, content) => {
    const trimmed = (content || '').trim();
    if (!trimmed) {
      return;
    }

    try {
      setSendingId(row.id);
      setError(null);
      await helpdeskAPI.addComment(row.id, { content: trimmed, is_internal: false });

      if (shouldSaveAsKnowledge(row.id)) {
        await helpdeskAPI.createKnowledgeEntry({
          question_summary: (row.customer_question || row.title || '').slice(0, 300),
          answer: trimmed,
          category_tag: row.ai_draft?.module || '',
          source_ticket: row.id,
        });
      }

      setEditingId(null);
      setEditText('');
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      console.error('Error sending reply from review queue:', err);
      setError(err.response?.data?.error || 'Failed to send reply');
    } finally {
      setSendingId(null);
    }
  };

  const handleApprove = (row) => {
    sendReply(row, row.ai_draft?.draft_reply);
  };

  const handleStartEdit = (row) => {
    setEditingId(row.id);
    setEditText(row.ai_draft?.draft_reply || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="text-center py-5">
          <Spinner animation="border" role="status" className="me-2" />
          <span>Loading review queue...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="fas fa-inbox me-2"></i>
          Review Queue
          <small className="text-muted fs-6 ms-2">
            {rows.length} waiting on a reply
          </small>
        </h2>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {rows.length === 0 ? (
        <Card>
          <Card.Body className="text-center text-muted py-5">
            <i className="fas fa-check-circle display-1 mb-3 text-success"></i>
            <h3>All caught up</h3>
            <p>Every customer message has a reply.</p>
          </Card.Body>
        </Card>
      ) : (
        <div className="d-grid gap-3">
          {rows.map((row) => {
            const isEditing = editingId === row.id;
            const isSending = sendingId === row.id;
            const draft = row.ai_draft?.draft_reply;

            return (
              <Card key={row.id} className="review-queue-card">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div>
                      <small className="text-primary fw-bold d-block">#{row.ticket_number}</small>
                      <strong>{row.title}</strong>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {row.ai_draft?.severity && (
                        <Badge bg={severityBadgeVariant(row.ai_draft.severity)}>
                          {row.ai_draft.severity}
                        </Badge>
                      )}
                      <Badge style={{ backgroundColor: row.priority_color || '#6c757d' }}>
                        {row.priority_name || 'Unknown'}
                      </Badge>
                    </div>
                  </div>

                  <div className="review-queue-question mb-2">
                    <small className="text-muted d-block">
                      {row.created_by_name} &middot; {new Date(row.customer_message_at).toLocaleString()}
                    </small>
                    <div>{row.customer_question}</div>
                  </div>

                  {draft ? (
                    <div className="review-queue-draft mb-2">
                      <small className="text-muted d-block mb-1">AI Draft Reply</small>
                      {isEditing ? (
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          disabled={isSending}
                        />
                      ) : (
                        <div className="ai-support-draft">{draft}</div>
                      )}
                    </div>
                  ) : (
                    <div className="text-muted mb-2">
                      <small>No AI draft yet for this ticket.</small>
                    </div>
                  )}

                  <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                    {isEditing ? (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => sendReply(row, editText)}
                          disabled={isSending || !editText.trim()}
                        >
                          {isSending ? 'Sending...' : 'Send Edited Reply'}
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={handleCancelEdit} disabled={isSending}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleApprove(row)}
                          disabled={isSending || !draft}
                        >
                          <i className="fas fa-check me-1"></i>
                          {isSending ? 'Sending...' : 'Approve & Send'}
                        </Button>
                        <Button variant="outline-primary" size="sm" onClick={() => handleStartEdit(row)} disabled={isSending}>
                          <i className="fas fa-pen me-1"></i>
                          Edit & Send
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => navigate(`/tickets/${row.id}`)}
                    >
                      <i className="fas fa-arrow-up-right-from-square me-1"></i>
                      Open Ticket
                    </Button>

                    <Form.Check
                      type="checkbox"
                      id={`save-as-knowledge-${row.id}`}
                      label="Save as knowledge"
                      checked={shouldSaveAsKnowledge(row.id)}
                      onChange={() => toggleSaveAsKnowledge(row.id)}
                      className="ms-auto small text-muted"
                    />
                  </div>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReviewQueue;
